import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Flex, Heading, Icon, SimpleGrid, Skeleton, Text } from "@chakra-ui/react";
import { ArrowRightIcon, ArrowUpRightIcon, Squares2X2Icon } from "@heroicons/react/24/outline";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { BASE_API_URL } from "./config";
import { a } from "./appColors";
import { formatExamBoard } from "./examBoards";

/**
 * The spaces you're in, inside the main app: where a student lands, and
 * one click from any of them into the space itself. The same list the
 * spaces dashboard shows, drawn in the app's own design system.
 */

const API = `${BASE_API_URL}/api/v1/user-course-links`;

// Who you are in a class decides which list and which page of it.
const AS = {
  STUDENT: { classPage: "/classroom/student/class", dashboard: "/classroom/student/dashboard" },
  INSTRUCTOR: { classPage: "/classroom/teacher/class", dashboard: "/classroom/teacher/dashboard" },
};

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("accessToken")}` });

const formatDue = (iso) =>
  new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });

/** Work still to hand in, per class, and when the soonest of it is due. */
function summariseWork(attempts) {
  const byCourse = {};
  attempts.forEach((w) => {
    if (w.submission_status === "SUBMITTED" || w.feedback_released) return;
    const s = (byCourse[w.course_guid] ||= { toDo: 0, nextDue: null });
    s.toDo += 1;
    if (w.due_date && (!s.nextDue || new Date(w.due_date) < new Date(s.nextDue))) s.nextDue = w.due_date;
  });
  return byCourse;
}

function Chip({ children, tone }) {
  const warm = tone === "warm";
  return (
    <Box
      as="span"
      display="inline-flex"
      alignItems="center"
      px="10px"
      py="3px"
      borderRadius="full"
      fontSize="12px"
      fontWeight="500"
      bg={warm ? a.warnBg : a.surfaceSubtle}
      color={warm ? a.warnText : a.textSub}
      border="1px solid"
      borderColor={warm ? a.chipWarnEdge : a.border}
      whiteSpace="nowrap"
    >
      {children}
    </Box>
  );
}

function ClassroomTile({ room, showWork, work, onOpen }) {
  const meta = [formatExamBoard(room.exam_board_code), room.exam_qualification_code, room.subject_name]
    .filter(Boolean)
    .join(" · ");
  return (
    <Box
      as="button"
      type="button"
      onClick={onOpen}
      role="group"
      position="relative"
      display="flex"
      flexDirection="column"
      alignItems="flex-start"
      textAlign="left"
      w="100%"
      minH="168px"
      p="20px"
      pb="56px"
      borderRadius="14px"
      border="1px solid"
      borderColor={a.border}
      bg={a.surface}
      transition="transform 0.2s, box-shadow 0.2s, background 0.2s"
      _hover={{
        bg: a.surfaceSubtle,
        transform: "translateY(-2px)",
        boxShadow: "0 12px 28px -14px rgba(34,34,34,0.35)",
      }}
      _active={{ transform: "translateY(0)" }}
      _focusVisible={{ outline: "none", boxShadow: "0 0 0 3px #00C9FE" }}
    >
      {room.subject_code && (
        <Box
          as="span"
          px="10px"
          py="3px"
          borderRadius="full"
          bg={a.blue}
          color="#000000"
          fontSize="12px"
          fontWeight="500"
        >
          {room.subject_code}
        </Box>
      )}
      <Text fontSize="16px" fontWeight="500" color={a.text} mt="12px" lineHeight="1.3" noOfLines={2}>
        {room.course_name}
      </Text>
      {meta && (
        <Text fontSize="13px" fontWeight="300" color={a.textSub} mt="4px" noOfLines={1}>
          {meta}
        </Text>
      )}
      {showWork && (
        <Flex gap="6px" mt="14px" wrap="wrap">
          {work?.toDo ? (
            <>
              <Chip tone="warm">{work.toDo} to do</Chip>
              {work.nextDue && <Chip>Next due {formatDue(work.nextDue)}</Chip>}
            </>
          ) : (
            <Chip>All caught up</Chip>
          )}
        </Flex>
      )}
      <Flex
        position="absolute"
        right="18px"
        bottom="18px"
        align="center"
        justify="center"
        boxSize="28px"
        borderRadius="full"
        bg={a.ink}
        color={a.onInk}
        transition="transform 0.2s"
        _groupHover={{ transform: "translateX(3px)" }}
      >
        <Icon as={ArrowRightIcon} w="14px" h="14px" strokeWidth={2.5} />
      </Flex>
    </Box>
  );
}

const ClassroomsPage = () => {
  const navigate = useNavigate();
  // The list is the student's unless the account is an instructor's.
  const userType = localStorage.getItem("userType") === "INSTRUCTOR" ? "INSTRUCTOR" : "STUDENT";
  const [rooms, setRooms] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    axios
      .get(`${API}/detail/?user_type=${userType}&limit=100`, { headers: authHeaders() })
      .then(({ data }) => live && setRooms(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error(err);
        if (live) setError("We couldn't load your spaces. Please refresh to try again.");
      });
    if (userType === "STUDENT") {
      axios
        .get(`${API}/user-paper-attempts/detail/?course_user_type=STUDENT&limit=200`, { headers: authHeaders() })
        .then(({ data }) => live && setAttempts(Array.isArray(data) ? data : []))
        .catch(() => {
          /* the counts are extra; the list stands without them */
        });
    }
    return () => {
      live = false;
    };
  }, [userType]);

  const work = useMemo(() => summariseWork(attempts), [attempts]);

  const open = (room) =>
    navigate(AS[userType].classPage, {
      state: { courseGuid: room.course_guid, classroomName: room.course_name, userType },
    });

  return (
    <Box w="100%" maxW="1200px">
      <Flex
        direction={{ base: "column", md: "row" }}
        align={{ base: "flex-start", md: "flex-end" }}
        justify="space-between"
        gap="16px"
        mb={{ base: 6, md: 8 }}
      >
        <Box>
          <Heading color={a.text} fontSize="24px" fontWeight="600">
            Spaces
          </Heading>
          <Text color={a.textSub} fontSize="15px" fontWeight="300" mt="4px">
            {userType === "INSTRUCTOR"
              ? "The spaces you teach. Pick one to go straight in."
              : "The spaces you're in. Pick one to go straight in."}
          </Text>
        </Box>
        {/* The spaces dashboard: everything across the spaces at once. */}
        <Button
          onClick={() => navigate(AS[userType].dashboard)}
          flexShrink={0}
          h="40px"
          px="16px"
          gap="8px"
          borderRadius="12px"
          bg={a.surface}
          color={a.text}
          border="1px solid"
          borderColor={a.border}
          fontSize="14px"
          fontWeight="500"
          _hover={{ bg: a.hover }}
          _active={{ bg: a.hover }}
          _focusVisible={{ boxShadow: "0 0 0 3px #00C9FE" }}
        >
          Spaces dashboard
          <Icon as={ArrowUpRightIcon} w="16px" h="16px" color={a.textSub} />
        </Button>
      </Flex>

      {error && (
        <Box
          p="14px 18px"
          borderRadius="14px"
          border="1px solid"
          borderColor={a.border}
          bg={a.badBg}
          color={a.badText}
          fontSize="14px"
          maxW="560px"
        >
          {error}
        </Box>
      )}

      {!error && rooms === null && (
        <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing="16px">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} h="168px" borderRadius="14px" startColor={a.surfaceSubtle} endColor={a.border} />
          ))}
        </SimpleGrid>
      )}

      {!error && rooms?.length === 0 && (
        <Flex
          direction="column"
          align="center"
          textAlign="center"
          py="56px"
          px="24px"
          borderRadius="14px"
          border="1px dashed"
          borderColor={a.border}
          bg={a.surface}
          maxW="640px"
        >
          <Flex boxSize="56px" borderRadius="16px" bg={a.blue} mb="16px" align="center" justify="center">
            <Icon as={Squares2X2Icon} w="26px" h="26px" color="#222222" strokeWidth={1.6} />
          </Flex>
          <Text fontSize="18px" fontWeight="600" color={a.text}>
            No spaces yet
          </Text>
          <Text fontSize="14px" fontWeight="300" color={a.textSub} mt="4px" maxW="380px">
            {userType === "INSTRUCTOR"
              ? "Spaces you create will appear here."
              : "Your teacher will send you an invite link. Open it, and the space will appear here."}
          </Text>
        </Flex>
      )}

      {!error && rooms?.length > 0 && (
        <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing="16px">
          {rooms.map((room) => (
            <ClassroomTile
              key={room.course_guid}
              room={room}
              showWork={userType === "STUDENT"}
              work={work[room.course_guid]}
              onOpen={() => open(room)}
            />
          ))}
        </SimpleGrid>
      )}
    </Box>
  );
};

export default ClassroomsPage;
